import { Check, Trash2 } from "lucide-react";
import { useState, type FC, type ReactNode } from "react";
import type {
  FieldValues,
  SubmitHandler,
  UseFormReturn
} from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { Form } from "./Form";

export type FormSheetSize = "md" | "lg" | "xl";

/*
 * `SheetContent` sizes itself with `data-[side=right]:…` utilities, which
 * outrank a plain `sm:max-w-*`; the `!` keeps these overrides winning.
 */
const SIZE_CLASS: Record<FormSheetSize, string> = {
  md: "sm:max-w-lg!",
  lg: "sm:max-w-xl!",
  xl: "sm:max-w-2xl!"
};

type FrameProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  size?: FormSheetSize;
  /** Ignore dismiss attempts (while a request is in flight). */
  busy?: boolean;
  /** Rendered inside `SheetFooter`; use `FormSheetActions` for the buttons. */
  footer?: ReactNode;
  className?: string;
  children: ReactNode;
};

/**
 * The side-panel shell every drawer editor shares: full width on phones,
 * capped on larger screens; header and footer pinned, body scrolls.
 */
export const FormSheetFrame: FC<FrameProps> = ({
  open,
  onOpenChange,
  title,
  description,
  size = "md",
  busy = false,
  footer,
  className,
  children
}) => (
  <Sheet
    open={open}
    onOpenChange={(next) => {
      if (!next && busy) return;
      onOpenChange(next);
    }}
  >
    <SheetContent
      className={cn(
        "flex w-full! flex-col gap-0 p-0",
        SIZE_CLASS[size],
        className
      )}
    >
      <SheetHeader className="shrink-0 border-b border-border pr-12">
        <SheetTitle>{title}</SheetTitle>
        {description ? (
          <SheetDescription>{description}</SheetDescription>
        ) : null}
      </SheetHeader>
      {children}
      {footer ? (
        <SheetFooter className="mt-0 shrink-0 flex-row items-center justify-between gap-2 border-t border-border">
          {footer}
        </SheetFooter>
      ) : null}
    </SheetContent>
  </Sheet>
);

/** Scrollable body padding for a `FormSheetFrame`. */
export const formSheetBodyClass =
  "flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 py-5";

type ActionsProps = {
  start?: ReactNode;
  children: ReactNode;
};

/** Footer layout: secondary things on the left, the buttons on the right. */
export const FormSheetActions: FC<ActionsProps> = ({ start, children }) => (
  <>
    <div className="flex min-w-0 items-center gap-2">{start}</div>
    <div className="flex shrink-0 items-center gap-2">{children}</div>
  </>
);

type Props<T extends FieldValues> = {
  form: UseFormReturn<T>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Runs with validated values; the sheet stays open until the caller closes it. */
  onSubmit: SubmitHandler<T>;
  /** Shows Delete and switches the default submit label. */
  isEditing?: boolean;
  submitLabel?: string;
  /** Defaults to `form.formState.isSubmitting`. */
  isSubmitting?: boolean;
  onDelete?: () => void;
  isDeleting?: boolean;
  deleteTitle?: string;
  deleteDescription?: ReactNode;
  /** Slot left of the buttons (e.g. status actions). */
  footerStart?: ReactNode;
  size?: FormSheetSize;
  className?: string;
  children: ReactNode;
};

/** Drawer editor for one record: the body is the form, the footer saves it. */
export function FormSheet<T extends FieldValues>({
  form,
  open,
  onOpenChange,
  title,
  description,
  onSubmit,
  isEditing = false,
  submitLabel,
  isSubmitting: isSubmittingProp,
  onDelete,
  isDeleting = false,
  deleteTitle = "Delete this entry?",
  deleteDescription,
  footerStart,
  size = "md",
  className,
  children
}: Props<T>) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isSubmitting = isSubmittingProp ?? form.formState.isSubmitting;
  const busy = isSubmitting || isDeleting;
  const { isDirty } = form.formState;
  const submit = form.handleSubmit(onSubmit);

  return (
    <>
      <FormSheetFrame
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        description={description}
        size={size}
        busy={busy}
        className={className}
        footer={
          <FormSheetActions
            start={
              <>
                {isEditing && onDelete ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    disabled={busy}
                    onClick={() => setConfirmOpen(true)}
                  >
                    <Trash2 className="size-3.5" />
                    Delete
                  </Button>
                ) : null}
                {footerStart}
              </>
            }
          >
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              size="sm"
              disabled={busy || (isEditing && !isDirty)}
              onClick={() => {
                submit().catch(() => undefined);
              }}
            >
              {isSubmitting ? (
                <Spinner className="size-3.5" />
              ) : (
                <Check className="size-3.5" />
              )}
              {submitLabel ?? (isEditing ? "Save changes" : "Add")}
            </Button>
          </FormSheetActions>
        }
      >
        <Form form={form} onSubmit={onSubmit} className={formSheetBodyClass}>
          {children}
          {/*
           * Lets Enter submit from a text input. Browsers skip a
           * `display: none` default button, so it's visually hidden instead.
           */}
          <button type="submit" className="sr-only" aria-hidden tabIndex={-1} />
        </Form>
      </FormSheetFrame>

      {onDelete ? (
        <ConfirmDeleteDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={deleteTitle}
          description={deleteDescription}
          isPending={isDeleting}
          onConfirm={() => {
            onDelete();
            setConfirmOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
